import React from 'react';
import { BiLoaderAlt, BiPause, BiPlay } from 'react-icons/bi';
import styled from 'styled-components';
import { useTTS } from '../../context/TTSProvider';
import { type ExtendedThemeConfig, useTheme } from '../../theme';

interface TTSButtonProps {
  /** Unique identifier for the block */
  blockId: string;
  /** Text content to convert to speech */
  text: string;
}

const TTSButton: React.FC<TTSButtonProps> = ({ blockId, text }) => {
  const theme = useTheme();
  const { play, pause, resume, isPlaying, currentPlayingBlockId, isPaused, loadingBlockId } =
    useTTS();

  const isThisBlockPlaying = isPlaying(blockId);
  const isThisBlockPaused = currentPlayingBlockId === blockId && isPaused;
  const isThisBlockLoading = loadingBlockId === blockId;
  const isActive = isThisBlockPlaying || isThisBlockPaused;

  const handleClick = async () => {
    if (!text || text.trim().length === 0) {
      console.warn('No text to convert to speech');
      return;
    }

    if (isThisBlockPlaying) {
      // Currently playing this block - pause it
      pause();
    } else if (isThisBlockPaused) {
      // This block is paused - resume it
      resume();
    } else {
      // Not playing or a different block - play this one
      await play(blockId, text);
    }
  };

  const getIcon = () => {
    if (isThisBlockLoading) {
      return <SpinningLoader />;
    }
    if (isThisBlockPlaying) {
      return <BiPause />;
    }
    return <BiPlay />;
  };

  const getTitle = () => {
    if (isThisBlockLoading) {
      return 'Loading audio...';
    }
    if (isThisBlockPlaying) {
      return 'Pause';
    }
    if (isThisBlockPaused) {
      return 'Resume';
    }
    return 'Play text as speech';
  };

  return (
    <StyledButton
      onClick={handleClick}
      disabled={isThisBlockLoading || !text}
      $theme={theme}
      $isActive={isActive}
      aria-label={getTitle()}
      title={getTitle()}
    >
      {getIcon()}
    </StyledButton>
  );
};

export default TTSButton;

const StyledButton = styled.button<{
  $theme: ExtendedThemeConfig;
  $isActive: boolean;
  disabled: boolean;
}>`
  background: none;
  border: none;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${(props) =>
    props.$isActive ? props.$theme.colors.primary : props.$theme.colors.sidebarGroupColor};
  opacity: ${(props) => (props.disabled ? 0.5 : props.$isActive ? 1 : 0.6)};
  font-size: 16px;

  &:hover {
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};
    background-color: ${(props) =>
      props.disabled
        ? 'transparent'
        : props.$theme.colors.messageBoxBorder || 'rgba(128, 128, 128, 0.1)'};
    transform: ${(props) => (props.disabled ? 'none' : 'scale(1.1)')};
  }

  &:active {
    transform: ${(props) => (props.disabled ? 'none' : 'scale(0.95)')};
  }

  svg {
    font-size: 16px;
  }
`;

const SpinningLoader = styled(BiLoaderAlt)`
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
